/**
 * Tiny fetch wrapper. Sends session cookies (`credentials: 'include'`) so
 * the Fastify backend can identify the user across API calls.
 *
 * Throws ApiError for non-2xx responses so React Query can distinguish
 * error states from data.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * One id per browser tab, sent with every request — live change events
 * carry it back, so the tab that made a change can skip its own (lib/live.ts).
 */
export const CLIENT_ID: string =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Client-Id': CLIENT_ID,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
    ...init,
  });
  const text = await res.text();
  const body: unknown = text ? safeParse(text) : null;
  if (!res.ok) {
    const bodyMsg =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : null;
    throw new ApiError(bodyMsg ?? `HTTP ${res.status}`, res.status, body);
  }
  return body as T;
}

function safeParse(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
